import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setCsrfToken, type AuthUser } from '../lib/api';

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** Resolves to whether an email confirmation step follows (false when the server has confirmation switched off). */
  signup: (email: string, password: string, fullName?: string) => Promise<{ verificationRequired: boolean }>;
  logout: () => Promise<void>;
  verifyEmail: (email: string, code: string) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const data = await api.getMe();
      setUser(data.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshUser();
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    const data = await api.login({ email, password });
    setCsrfToken(data.csrfToken);
    setUser(data.user);
  }, []);

  const signup = useCallback(async (email: string, password: string, fullName?: string) => {
    const result = await api.signup({ email, password, fullName });
    return { verificationRequired: result?.verificationRequired !== false };
  }, []);

  const logout = useCallback(async () => {
    await api.logout();
    setCsrfToken(null);
    setUser(null);
  }, []);

  const verifyEmail = useCallback(async (email: string, code: string) => {
    await api.verifyEmail({ email, code });
  }, []);

  const resendVerification = useCallback(async (email: string) => {
    await api.resendVerification(email);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      login,
      signup,
      logout,
      verifyEmail,
      resendVerification,
      refreshUser
    }),
    [user, loading, login, signup, logout, verifyEmail, resendVerification, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}

/** Like useAuth but returns null outside an AuthProvider (public pages and isolated tests). */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}
